import { useEffect, useRef, useState } from "react";
import type { CSSProperties, FocusEvent } from "react";
import { getCatalog, localeLabels } from "../i18n";
import { useNexusStore } from "../store/nexusStore";

const localeOrder = ["en", "pl", "de"] as const;

export function LocaleSwitch() {
  const locale = useNexusStore((state) => state.locale);
  const setLocale = useNexusStore((state) => state.setLocale);
  const ui = getCatalog(locale).ui;
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelCollapse = () => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const scheduleCollapse = () => {
    cancelCollapse();
    closeTimerRef.current = window.setTimeout(() => {
      setExpanded(false);
      closeTimerRef.current = null;
    }, 140);
  };

  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setExpanded(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [expanded]);

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const activeIndex = localeOrder.indexOf(locale);
  const style = {
    ["--kx-locale-step" as string]: "48px",
    ["--kx-locale-track-shift" as string]: `${-activeIndex * 48}px`,
    ["--kx-locale-thumb-shift" as string]: `${activeIndex * 48}px`,
  } as CSSProperties;

  const handlePress = (value: (typeof localeOrder)[number]) => {
    cancelCollapse();
    if (!expanded && value === locale) {
      setExpanded(true);
      return;
    }
    setLocale(value);
    setExpanded(false);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!next || !event.currentTarget.contains(next)) scheduleCollapse();
  };

  return (
    <div
      ref={rootRef}
      className={`locale-switch${expanded ? " is-expanded" : ""}`}
      role="group"
      aria-label={ui.languageSelector}
      onMouseEnter={() => {
        cancelCollapse();
        setExpanded(true);
      }}
      onMouseLeave={scheduleCollapse}
      onFocus={() => {
        cancelCollapse();
        setExpanded(true);
      }}
      onBlur={handleBlur}
      style={style}
    >
      <div className="locale-switch__track">
        <div className="locale-switch__thumb" aria-hidden="true" />
        {localeOrder.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={value === locale}
            className={value === locale ? "is-active" : ""}
            onClick={() => handlePress(value)}
            aria-label={ui.switchLanguage(localeLabels[value])}
          >
            {localeLabels[value]}
          </button>
        ))}
      </div>
    </div>
  );
}
